import { useState } from "react";
import { ChevronDown, LayoutTemplate, Loader2, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApiSWR } from "@/hooks/useApiSWR";
import {
	type ChecklistTemplate,
	listChecklistTemplates,
} from "@/services/task-checklist.service";
import { reportError } from "../utils";

export function ChecklistTemplateMenu({
	projectId,
	onApply,
}: {
	projectId: string;
	onApply: (templateId: string) => Promise<void>;
}) {
	const { data, isLoading } = useApiSWR<ChecklistTemplate[]>(
		["checklist-templates", projectId],
		() => listChecklistTemplates(projectId),
	);
	const [applying, setApplying] = useState(false);

	const templates = data ?? [];

	async function handleApply(id: string) {
		setApplying(true);
		try {
			await onApply(id);
		} catch (e) {
			reportError(e);
		} finally {
			setApplying(false);
		}
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" size="sm" disabled={applying}>
					{applying ? (
						<Loader2 className="h-3 w-3 mr-1 animate-spin" />
					) : (
						<LayoutTemplate className="h-3 w-3 mr-1" />
					)}
					Use template
					<ChevronDown className="h-3 w-3 ml-1" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuLabel>Checklist templates</DropdownMenuLabel>
				<DropdownMenuSeparator />
				{isLoading ? (
					<DropdownMenuItem disabled>Loading...</DropdownMenuItem>
				) : templates.length === 0 ? (
					<DropdownMenuItem disabled>No templates</DropdownMenuItem>
				) : (
					templates.map((t) => (
						<DropdownMenuItem
							key={t.id}
							onSelect={() => handleApply(t.id)}
						>
							<span className="truncate">{t.name}</span>
							<span className="ml-auto text-xs text-muted">
								{t.items.length}
							</span>
						</DropdownMenuItem>
					))
				)}
				<DropdownMenuSeparator />
				<DropdownMenuItem
					onSelect={() =>
						window.open(
							`/settings/checklist-templates?project=${projectId}`,
							"_blank",
							"noopener,noreferrer",
						)
					}
				>
					<Settings2 className="h-3.5 w-3.5 mr-2" />
					Manage templates
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
